import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    TableSortLabel,
    Pagination,
    Stack,
    Typography,
    Link,
    Box,
    Chip,
    IconButton,
    Alert,
    Card,
    CardContent,
    InputAdornment,
    Tooltip,
    Badge,
    Skeleton,
    Divider,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import { 
    Search as SearchIcon, 
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    Visibility as VisibilityIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Campaign as CampaignIcon,
    LocalOffer as CouponIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
} from "@mui/icons-material";
import { getUsedCouponsListByCompany, getAdminCompanyId } from "../../../api/api";
import AdminHeader from "../../../components/AdminHeader/AdminHeader";
import Cookies from "js-cookie";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "@mui/material/styles";
import { MAIN_URL } from "../../../api/api";
import { formatAdminDate } from "../../../utils/formatDate";
import { getAdminNavLinks } from "../../../theme/adminNavLinks";
import { pageBackgroundSx } from "../../../theme/theme";
import { ADMIN_PAGE_GUIDES } from "../../../constants/adminPageGuides";
import { getCouponImageStyles } from "../../../constants/imageSizes";
import { useLocation } from "react-router-dom";

// Styled components for better visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
}));

// const StyledButton = styled(Button)(({ theme }) => ({
//     borderRadius: 8,
//     textTransform: 'none',
//     fontWeight: 600,
//     padding: '10px 24px',
//     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
//     transition: 'all 0.3s ease-in-out',
//     '&:hover': {
//         transform: 'translateY(-2px)',
//         boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
//     },
// }));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: '#ffffff',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
        },
    },
}));

const sortByFirstCreated = (rows, ascending = false) =>
    [...rows].sort((a, b) => {
        const da = a.firstCreated ? new Date(a.firstCreated).getTime() : 0;
        const db = b.firstCreated ? new Date(b.firstCreated).getTime() : 0;
        return ascending ? da - db : db - da;
    });

const CouponUsed = () => {
    const location = useLocation();
    const [openRows, setOpenRows] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [coupons, setCoupons] = useState([]);
    const [allCoupons, setAllCoupons] = useState([]); // Store all coupons for filtering
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [sortAsc, setSortAsc] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyName, setCompanyName] = useState("");
    const [adminName, setAdminName] = useState("");
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCoupons: 0,
        totalCampaigns: 0
    });

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const admin_id = Cookies.get("admin_id");
                if (admin_id) {
                    const response = await getAdminCompanyId(admin_id);
                    setCompanyName(response.data.company.name);
                    setAdminName(response.data.admin.name);
                }
            } catch (error) {
                console.error("Error fetching company:", error);
            }
        };
        
        fetchCompany();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                // Try to get company ID from cookie or location state first
                let companyId = Cookies.get("admin_company_id") || location.state?.admin_company_id;

                console.log("Used Coupons - Cookie admin_company_id:", Cookies.get("admin_company_id"));
                console.log("Used Coupons - Location state admin_company_id:", location.state?.admin_company_id);
                console.log("Used Coupons - Initial company ID:", companyId);

                // Fallback: If no company ID, try to fetch it using admin_id (like List page does)
                if (!companyId) {
                    const adminId = Cookies.get("admin_id");
                    console.log("Used Coupons - No company ID found, trying to fetch using admin_id:", adminId);
                    
                    if (adminId) {
                        try {
                            const response = await getAdminCompanyId(adminId);
                            companyId = response.data.company.id;
                            console.log("Used Coupons - Fetched company ID from API:", companyId);
                            
                            // Set the cookie so we don't have to fetch again
                            Cookies.set("admin_company_id", companyId, { expires: 90, path: '/', sameSite: 'lax' });
                        } catch (error) {
                            console.error("Used Coupons - Failed to fetch company ID:", error);
                            setError("Company ID not found. Please login again.");
                            setLoading(false);
                            return;
                        }
                    } else {
                        console.log("Used Coupons - No admin_id found either");
                        setError("Company ID not found. Please login again.");
                        setLoading(false);
                        return;
                    }
                }

                if (!companyId) {
                    console.log("Used Coupons - Still no company ID found after all attempts");
                    setError("Company ID not found. Please login again.");
                    setLoading(false);
                    return;
                }

                console.log("Used Coupons - Fetching data for company:", companyId, "Page:", page, "Per page:", rowsPerPage);
                // When searching, fetch all data (use large per_page). Otherwise use pagination
                const fetchPerPage = searchTerm.trim() ? 10000 : rowsPerPage;
                const fetchPage = searchTerm.trim() ? 1 : page;
                const res = await getUsedCouponsListByCompany(companyId, fetchPage, fetchPerPage);
                const grouped = res?.data?.data || {};
                const paginationData = res?.data?.pagination || {};

                // Convert object -> array & compute fields the table expects
                const rows = Object.entries(grouped).map(([mobile, r]) => {
                    const campaigns = Array.isArray(r.Campaigns) ? r.Campaigns : [];
                    const allUserCoupons = campaigns.flatMap((c) => c.Coupons || []);
                    const firstCreated =
                        r.FirstCreated ||
                        allUserCoupons
                            .map((c) => c.CouponCreated)
                            .filter(Boolean)
                            .sort()[0] ||
                        null;

                    return {
                        mobileNr: mobile,
                        userName: r.UserName || "",
                        campaigns,
                        firstCreated,
                        usedCount: r.UsedCouponCount ?? 0,
                        maxExpiryDate: r.MaxExpiryDate || null,
                    };
                });

                const sortedRows = sortByFirstCreated(rows, sortAsc);

                // Calculate statistics (from current page only)
                const totalCoupons = sortedRows.reduce((sum, row) => sum + row.usedCount, 0);
                const totalCampaigns = new Set(
                    sortedRows.flatMap(row => 
                        row.campaigns.map(c => c.CampaignName).filter(Boolean)
                    )
                ).size;

                setStats({
                    totalUsers: paginationData.total || sortedRows.length,
                    totalCoupons,
                    totalCampaigns
                });

                setAllCoupons(sortedRows); // Store all fetched coupons
                // Only set coupons directly if not searching (filter useEffect will handle search case)
                if (!searchTerm.trim()) {
                    setPagination(paginationData);
                    setCoupons(sortedRows);
                }
            } catch (err) {
                console.error("Error fetching used coupons:", err);
                const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to load coupon data. Please try again.";
                const statusCode = err.response?.status;
                const debugInfo = statusCode ? ` (Status: ${statusCode})` : '';
                setError(errorMessage + debugInfo);
                setCoupons([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [page, rowsPerPage, location.state?.admin_company_id, searchTerm]);

    const toggleRow = (mobile) => {
        setOpenRows((prev) => ({ ...prev, [mobile]: !prev[mobile] }));
    };

    // Client-side filtering and pagination based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            // If no search term, use backend pagination (data already paginated)
            // Don't re-paginate, just use what was fetched
            return;
        } else {
            // Filter coupons based on search term
            const searchLower = searchTerm.toLowerCase().trim();
            const filtered = allCoupons.filter(row => {
                // Search in mobile number
                const mobileMatch = row.mobileNr?.toLowerCase().includes(searchLower);
                // Search in user name
                const nameMatch = row.userName?.toLowerCase().includes(searchLower);
                // Search in campaign names
                const campaignMatch = row.campaigns?.some(campaign => 
                    campaign.CampaignName?.toLowerCase().includes(searchLower)
                );
                return mobileMatch || nameMatch || campaignMatch;
            });

            // Apply pagination to filtered results
            const offset = (page - 1) * rowsPerPage;
            const paginated = sortByFirstCreated(filtered, sortAsc).slice(
                offset,
                offset + rowsPerPage
            );
            setCoupons(paginated);
            setPagination({
                current_page: page,
                per_page: rowsPerPage,
                total: filtered.length,
                last_page: Math.ceil(filtered.length / rowsPerPage),
                from: filtered.length > 0 ? offset + 1 : 0,
                to: Math.min(offset + rowsPerPage, filtered.length)
            });
        }
    }, [searchTerm, allCoupons, page, rowsPerPage, sortAsc]);

    // Reset to page 1 when search term changes
    useEffect(() => {
        if (searchTerm.trim() && page !== 1) {
            setPage(1);
        }
    }, [searchTerm]);

    const handleRefresh = () => {
        window.location.reload();
    };

    const handleListAll = () => {
        setSearchTerm("");
        setPage(1);
        // This will trigger a refetch via the useEffect dependency on page
    };

    const handleSortByDate = () => {
        const nextAsc = !sortAsc;
        setSortAsc(nextAsc);
        setCoupons((prev) => sortByFirstCreated(prev, nextAsc));
        setAllCoupons((prev) => sortByFirstCreated(prev, nextAsc));
    };

    // No need for client-side pagination - backend handles it
    const paginatedCoupons = Array.isArray(coupons) ? coupons : [];

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <Box sx={{ width: "100%" }}>
            {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
            ))}
        </Box>
    );

    // Empty state component
    const EmptyState = () => (
        <Box sx={{ textAlign: "center", py: 8 }}>
            <CouponIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
                No Used Coupons Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {searchTerm ? "Try adjusting your search criteria" : "No coupons have been used yet"}
            </Typography>
        </Box>
    );

    return (
            <Box sx={pageBackgroundSx}>
                <Box sx={{ maxWidth: { xs: "100%", sm: "95%" }, mx: "auto", p: { xs: 1, sm: 2 } }}>
                    <AdminHeader 
                        title="Used Coupons Report"
                        icon={CouponIcon}
                        navigationLinks={getAdminNavLinks("/used")}
                        guide={ADMIN_PAGE_GUIDES.used}
                    />

                    {/* Statistics and Search Section */}
                    <StyledCard sx={{ mb: { xs: 2, sm: 3 } }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' }, 
                                justifyContent: 'space-between',
                                flexWrap: 'wrap', 
                                gap: { xs: 2, sm: 2 } 
                            }}>
                                {/* Statistics */}
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    <Chip 
                                        icon={<PeopleIcon />} 
                                        label={`${stats.totalUsers} Users`} 
                                        color="primary" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                    <Chip 
                                        icon={<TrendingUpIcon />} 
                                        label={`${stats.totalCoupons} Used`} 
                                        color="primary" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                    <Chip 
                                        icon={<CampaignIcon />} 
                                        label={`${stats.totalCampaigns} Campaigns`} 
                                        color="primary" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                </Stack>

                                {/* Search Bar */}
                                <StyledTextField
                                    placeholder="Search mobile, name, campaign..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { xs: 0, sm: 250 } }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Clear Search">
                                        <IconButton 
                                            onClick={handleListAll} 
                                            disabled={!searchTerm}
                                            size="small"
                                        >
                                            <FilterIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Refresh Data">
                                        <IconButton onClick={handleRefresh} size="small">
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </CardContent>
                    </StyledCard>

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} action={
                        <Button color="inherit" size="small" onClick={handleRefresh}>
                            Retry
                        </Button>
                    }>
                        {error}
                    </Alert>
                )}

                {/* Main Content */}
                <StyledCard>
                    <CardContent sx={{ p: 0 }}>
                        {loading ? (
                            <Box sx={{ p: 2 }}>
                                <LoadingSkeleton />
                            </Box>
                        ) : coupons.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <>
                                {/* Table Header */}
                                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Coupon Usage Details
                                        </Typography>
                                        <Badge badgeContent={coupons.length} color="primary">
                                            <VisibilityIcon color="action" />
                                        </Badge>
                    </Stack>
                                </Box>

                    {/* Table */}
                                <TableContainer sx={{ 
                                    maxHeight: { xs: 400, sm: 600 },
                                    overflow: 'auto',
                                    overflowX: 'auto',
                                    maxWidth: '100%',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#c1c1c1',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: '#a8a8a8',
                                        },
                                    },
                                }}>
                                    <Table size="small" sx={{ tableLayout: { xs: 'auto', md: 'fixed' }, minWidth: 720 }}>
                            <TableHead>
                                            <TableRow sx={{ bgcolor: "grey.100" }}>
                                                <TableCell sx={{ fontWeight: "bold", width: 60, padding: "8px 16px" }}>Actions</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>Mobile No</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>User Name</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 150, padding: "8px 16px" }}>Campaigns</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>
                                        <TableSortLabel
                                            active
                                            direction={sortAsc ? "asc" : "desc"}
                                            onClick={handleSortByDate}
                                        >
                                                        First Created
                                        </TableSortLabel>
                                    </TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 100, padding: "8px 16px" }}>Used Count</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>Last Expiry</TableCell>
                                </TableRow>
                            </TableHead>
                          <TableBody>
                                            {paginatedCoupons.map((row) => {
                                                const campaigns = row.campaigns || [];
                                                const firstCreated = row.firstCreated || "-";

                                                const visibleCampaigns = campaigns.slice(0, 3);
                                                const extraCount = campaigns.length - 3;

                                                return (
                                                    <React.Fragment key={row.mobileNr}>
                                                        {/* Main Row */}
                                                        <TableRow
                                                            hover
                                                            onClick={() => toggleRow(row.mobileNr)}
                                                            sx={{ 
                                                                cursor: "pointer",
                                                                "&:hover": { bgcolor: "action.hover" }
                                                            }}
                                                        >
                                                            <TableCell sx={{ width: 60, padding: "8px 16px" }}>
                                                                <Tooltip title={openRows[row.mobileNr] ? "Collapse" : "Expand"}>
                                                                    <IconButton size="small" color="primary">
                                                                        {openRows[row.mobileNr] ? (
                                                                            <KeyboardArrowUpIcon />
                                                                        ) : (
                                                                            <KeyboardArrowDownIcon />
                                                                        )}
                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {row.mobileNr}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2">
                                                                    {row.userName || "N/A"}
                                                                </Typography>
                                    </TableCell>
                                                            <TableCell sx={{ width: 150, padding: "8px 16px" }}>
                                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                                    {visibleCampaigns.map((c, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={c.CampaignName || "Single Coupon"}
                                                                            size="small"
                                                                            color="primary"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                    {extraCount > 0 && (
                                                                        <Chip
                                                                            label={`+${extraCount} more`}
                                                                            size="small"
                                                                            color="default"
                                                                            variant="outlined"
                                                                        />
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {formatAdminDate(firstCreated)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 100, padding: "8px 16px" }}>
                                                                <Badge badgeContent={row.usedCount} color="success">
                                                                    <CouponIcon color="action" />
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {formatAdminDate(row.maxExpiryDate)}
                                                                </Typography>
                                    </TableCell>
                                </TableRow>

                                                        {/* Expanded Drilldown Rows */}
                                                        {openRows[row.mobileNr] && campaigns.map((campaign, campaignIndex) =>
                                                            (campaign.Coupons || []).map((coupon, couponIndex) => (
                                                                <TableRow 
                                                                    key={`${campaignIndex}-${couponIndex}`}
                                                                    sx={{ bgcolor: "grey.50" }}
                                                                >
                                                                    {/* Actions Column - Empty but maintains alignment */}
                                                                    <TableCell sx={{ width: 60, padding: "8px 16px" }}></TableCell>
                                                                    
                                                                    {/* Mobile No Column - Empty but maintains alignment */}
                                                                    <TableCell sx={{ width: 120, padding: "8px 16px" }}></TableCell>
                                                                    
                                                                    {/* User Name Column - Empty but maintains alignment */}
                                                                    <TableCell sx={{ width: 120, padding: "8px 16px" }}></TableCell>
                                                                    
                                                                    {/* Campaign Column - Show campaign name only on first coupon */}
                                                                    <TableCell sx={{ width: 150, padding: "8px 16px" }}>
                                                                        {couponIndex === 0 && (
                                                                            <Typography variant="body2" fontWeight="medium" color="primary">
                                                                                {campaign.CampaignName || "Single Coupon"}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                    
                                                                    {/* First Created Column - Show coupon created date */}
                                                                    <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {formatAdminDate(coupon.CouponCreated)}
                                            </Typography>
                                                                    </TableCell>
                                                                    
                                                                    {/* Used Count Column - Show coupon image */}
                                                                    <TableCell sx={{ width: 100, padding: "8px 16px" }}>
                                                                        <Tooltip title="Click to view full size">
                                                                            <Box
                                                                                component="img"
                                                                                src={`${MAIN_URL}/images/${coupon.ImageUrl}`}
                                                                                alt="coupon"
                                                                                loading="lazy"
                                                                                decoding="async"
                                                                                sx={getCouponImageStyles('SMALL')}
                                                                            />
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    
                                                                    {/* Last Expiry Column - Show coupon expiry date */}
                                                                    <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {formatAdminDate(coupon.ExpiryDate)}
                                                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                                            ))
                                                        )}
                                </React.Fragment>
                                                );
                                            })}
                          </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                                <Box sx={{ p: { xs: 1, sm: 1.5 }, borderTop: 1, borderColor: "divider" }}>
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        justifyContent="space-between" 
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        spacing={{ xs: 2, sm: 2 }}
                                    >
                                        {/* Left side - Info and items per page */}
                                        <Stack 
                                            direction={{ xs: 'column', sm: 'row' }} 
                                            spacing={{ xs: 1, sm: 2 }}
                                            alignItems={{ xs: 'center', sm: 'center' }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ 
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    textAlign: { xs: 'center', sm: 'left' }
                                                }}
                                            >
                                                {pagination.total > 0 ? (
                                                    `Showing ${pagination.from} to ${pagination.to} of ${pagination.total} entries`
                                                ) : (
                                                    'No entries found'
                                                )}
                                            </Typography>
                                            {pagination.total > 0 && (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        Items per page:
                                                    </Typography>
                                                    <FormControl size="small" sx={{ minWidth: 70 }}>
                                                        <Select
                                                            value={rowsPerPage}
                                                            onChange={(e) => {
                                                                setRowsPerPage(Number(e.target.value));
                                                                setPage(1); // Reset to first page when changing items per page
                                                            }}
                                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, height: '32px' }}
                                                        >
                                                            <MenuItem value={10}>10</MenuItem>
                                                            <MenuItem value={25}>25</MenuItem>
                                                            <MenuItem value={50}>50</MenuItem>
                                                            <MenuItem value={100}>100</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Stack>
                                            )}
                                        </Stack>

                                        {/* Right side - Page selection */}
                                        {pagination.total > 0 && pagination.last_page > 1 && (
                                            <Stack 
                                                direction="row" 
                                                spacing={1}
                                                alignItems="center"
                                                justifyContent={{ xs: 'center', sm: 'flex-end' }}
                                                sx={{ flexWrap: 'wrap', gap: 1 }}
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page === 1}
                                                    onClick={() => setPage(1)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    First
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page === 1}
                                                    onClick={() => setPage(page - 1)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    Prev
                                                </Button>
                                                
                                                {/* Page number buttons */}
                                                <Pagination
                                                    count={pagination.last_page}
                                                    page={page}
                                                    onChange={(event, value) => setPage(value)}
                                                    color="primary"
                                                    size="small"
                                                    showFirstButton
                                                    showLastButton
                                                    siblingCount={1}
                                                    boundaryCount={1}
                                                    sx={{
                                                        '& .MuiPaginationItem-root': {
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                            minWidth: { xs: '28px', sm: '32px' },
                                                            height: { xs: '28px', sm: '32px' },
                                                        }
                                                    }}
                                                />
                                                
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page >= pagination.last_page}
                                                    onClick={() => setPage(page + 1)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    Next
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page >= pagination.last_page}
                                                    onClick={() => setPage(pagination.last_page)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    Last
                                                </Button>
                                                
                                                {/* Jump to page input */}
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }}>
                                                        Go to:
                                                    </Typography>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={page}
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value);
                                                            if (value >= 1 && value <= pagination.last_page) {
                                                                setPage(value);
                                                            }
                                                        }}
                                                        inputProps={{
                                                            min: 1,
                                                            max: pagination.last_page,
                                                            style: { 
                                                                textAlign: 'center',
                                                                fontSize: '0.75rem',
                                                                padding: '4px 8px',
                                                                width: '50px'
                                                            }
                                                        }}
                                                        sx={{
                                                            width: '60px',
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '32px',
                                                            }
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                        / {pagination.last_page}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        )}
                                </Stack>
                                </Box>
                            </>
                        )}
                    </CardContent>
                </StyledCard>
            </Box>
        </Box>
    );
};

export default CouponUsed;
